import { z } from 'zod';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FormState } from 'react-hook-form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    AmountPart,
    GeneralPricingMode,
    Product,
    ProductAddParameters,
    ProductChargingMode,
    ProductConsumptionMoment,
    ProductPricing,
    ProductUpdateParameters,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { WithProductId } from '@mews-commander/types';
import { getBase64 } from '@mews/framework';
import { useCurrentEnterpriseTaxations } from '@mews-commander/legacy-entities/src/selectors/taxEnvironments';
import { useCurrentAccountingEnvironment } from '../../hooks/useCurrentAccountingEnvironment';
import { useIsAccountingCategorySetupRequired } from '../../queries/useIsAccountingCategorySetupRequired';
import { useDetailsSchema } from './detailsSchema';
import { productPricingSchema } from './pricingSchema';
import { productOptionsSchema } from './optionsSchema';
import { useAccountingSchema } from './accountingSchema';

export const useProductFormSchema = () => {
    const detailsSchema = useDetailsSchema();
    const accountingSchema = useAccountingSchema();
    return detailsSchema.merge(productPricingSchema).merge(productOptionsSchema).merge(accountingSchema);
};

export type ProductFormValues = z.input<ReturnType<typeof useProductFormSchema>>;

export const useProductCreateFormDefaultValues = (): ProductFormValues => {
    const { id, defaultCurrencyCode, defaultLanguageCode } = useCurrentEnterprise();
    const taxations = useCurrentEnterpriseTaxations();

    const { data: defaultAccountingCategoryId } = useRepositorySuspenseQuery(
        'accountingCategories',
        'getAll',
        { enterpriseIds: [id] },
        { select: ({ accountingCategories }) => accountingCategories?.[0].id }
    );
    const isAccountingCategorySetupRequired = useIsAccountingCategorySetupRequired();

    return {
        names: {
            [defaultLanguageCode]: '',
        },
        productCategoryId: undefined,
        ordering: 0,
        pricing: {
            type: ProductPricing.Absolute,
            amount: 0,
            currency: defaultCurrencyCode,
            charging: ProductChargingMode.Once,
        },
        taxRates: pipe(
            taxations,
            array.reduce({}, (taxRates: ProductFormValues['taxRates'], { code, taxRateCodes }) => {
                if (code && taxRateCodes && array.isNonEmpty(taxRateCodes)) {
                    taxRates[code] = '';
                }
                return taxRates;
            })
        ),
        consumptionMoment: ProductConsumptionMoment.ServiceOrderStart,
        options: {
            offerToCustomer: true,
            offerToEmployee: true,
            createTask: true,
        },
        accountingCategoryId: isAccountingCategorySetupRequired ? defaultAccountingCategoryId : undefined,
        rebateAccountingCategoryId: isAccountingCategorySetupRequired ? defaultAccountingCategoryId : undefined,
    };
};

export const useToProductEditFormDefaultValues = () => {
    const taxations = useCurrentEnterpriseTaxations();
    return ({ pricing, chargingMode, postingMode, consumptionMoment, ...other }: Product): ProductFormValues => ({
        ...other,
        pricing:
            pricing.discriminator === ProductPricing.Absolute
                ? {
                      type: pricing.discriminator,
                      amount: pricing.value.value,
                      currency: pricing.value.currency,
                      charging: chargingMode,
                  }
                : {
                      type: pricing.discriminator,
                      percentage: pricing.value.multiplier as number,
                      value: pricing.value.target as AmountPart.GrossValue,
                      products: pricing.value.productIds as [string, ...string[]],
                      charging: postingMode,
                  },
        taxRates: pipe(
            taxations,
            array.reduce({}, (currentTaxRates: ProductFormValues['taxRates'], currentTaxation) =>
                pipe(
                    option.Do,
                    option.apS('code', option.fromNullable(currentTaxation.code)),
                    option.apS('taxRateCodes', option.fromNullable(currentTaxation.taxRateCodes)),
                    option.chain((taxation) =>
                        pipe(
                            taxation.taxRateCodes,
                            array.findFirst((taxRateCode) =>
                                pipe(
                                    pricing.discriminator === ProductPricing.Absolute
                                        ? pricing.value.taxValues?.map((taxValue) => taxValue.taxRateCode)
                                        : pricing.value.taxRateCodes,
                                    option.fromNullable,
                                    option.map((taxRateCodes) => taxRateCodes.includes(taxRateCode)),
                                    option.getOrElse(() => false)
                                )
                            ),
                            (value) => {
                                currentTaxRates[taxation.code] = option.isSome(value) ? value.value : null;
                                return option.some(currentTaxRates);
                            }
                        )
                    ),
                    option.getOrElse(() => currentTaxRates)
                )
            )
        ),
        consumptionMoment: consumptionMoment as ProductConsumptionMoment,
    });
};

const useToPricingParameter = () => {
    const { pricing: pricingMode } = useCurrentEnterprise();
    return ({ pricing, taxRates }: Pick<ProductFormValues, 'pricing' | 'taxRates'>) => {
        const taxRateCodes = pipe(
            Object.values(taxRates),
            array.filterMap((taxRateCode) => (taxRateCode ? option.some(taxRateCode) : option.none))
        );
        return pricing.type === ProductPricing.Absolute
            ? {
                  discriminator: pricing.type,
                  value: {
                      ...(pricingMode === GeneralPricingMode.Net
                          ? { netValue: pricing.amount }
                          : { grossValue: pricing.amount }),
                      value: pricing.amount,
                      currency: pricing.currency,
                      taxValues: pipe(
                          taxRateCodes,
                          array.map((taxRateCode) => ({ taxRateCode, value: 0 }))
                      ),
                  },
              }
            : {
                  discriminator: pricing.type,
                  value: {
                      multiplier: pricing.percentage,
                      productIds: pricing.products,
                      target: pricing.value,
                      taxRateCodes,
                  },
              };
    };
};

export const useToProductAddParameters = () => {
    const accountingEnvironment = useCurrentAccountingEnvironment();

    const productFormSchema = useProductFormSchema();
    const toPricingParameter = useToPricingParameter();

    return async (formValues: ProductFormValues) =>
        productFormSchema
            .transform<ProductAddParameters>(async ({ image, units, pricing, taxRates, ...values }) => {
                const parameters = {
                    ...values,
                    image:
                        image && image[0]
                            ? {
                                  name: image[0].name,
                                  type: image[0].type,
                                  data: await getBase64(image[0]),
                              }
                            : null,
                    pricing: toPricingParameter({ pricing, taxRates }),
                    chargingMode: pricing.type === ProductPricing.Absolute ? pricing.charging : null,
                    postingMode: pricing.type === ProductPricing.Relative ? pricing.charging : null,
                };
                return accountingEnvironment.useBillingUnits ? { ...parameters, units } : parameters;
            })
            .parseAsync(formValues);
};

export const useToProductUpdateParameters = ({ productId }: WithProductId) => {
    const toProductAddParameters = useToProductAddParameters();

    return async (formValues: ProductFormValues, dirtyFields: FormState<ProductFormValues>['dirtyFields']) =>
        pipe(
            await toProductAddParameters(formValues),
            Object.entries,
            array.map(([parameter, value]) => [parameter, { value }]),
            array.filter(([parameter]) => (parameter === 'image' ? !!dirtyFields.image : true)),
            Object.fromEntries,
            (parameters): ProductUpdateParameters => ({
                ...parameters,
                productId,
            })
        );
};
